import { captureException } from '@sentry/react';
import { z } from 'zod';

import { LiftAPIClientError } from '~/data/liftApiMutator';

/**
 * Returns a new `URLSearchParams` with a `to` param added to it. If a base
 * `URLSearchParams` is provided in the `searchParams` argument, the returned
 * `URLSearchParams` instance will include its original values as well as the new
 * `to` param.
 *
 * @param to The value of the `to` param. Defaults to `/`.
 * @param searchParams The search params to add the `to` param to. If not provided a
 * new `URLSearchParams` will be created.
 * @returns A new `URLSearchParams` instance with a `to` param added.
 */
export function withLoginRedirectSearchParam(to = '/', searchParams?: URLSearchParams) {
  const params = searchParams ?? new URLSearchParams();
  params.set('to', to);

  return params;
}

/**
 * Load `path` as a new document. Used when the signed-in account changes, so
 * nothing the previous account loaded (SWR caches, React state, the analytics
 * identity) carries over into the next one.
 */
export const loadDocument = (path: string) => {
  window.location.assign(path);
};

/**
 * Load the current URL as a new document. `loadDocument` cannot do this: when
 * the URL has a fragment, assigning it again only scrolls to the fragment.
 */
export const reloadDocument = () => {
  window.location.reload();
};

export const IdentityEmail = z.string({ required_error: 'The email is required' }).email();
export const IdentityEmailForm = z.object({
  email: IdentityEmail,
});
export type IdentityEmailForm = z.infer<typeof IdentityEmailForm>;
const IdentityPassword = z
  .string({ required_error: 'The password is required' })
  .min(8, { message: 'The password must be at least 8 characters long' })
  // Imported Firebase passwords can be longer than a form would suggest; the cap
  // only stops absurd inputs.
  .max(128, { message: 'The password must be at most 128 characters long' })
  .refine((password) => /[0-9]/.test(password), {
    message: 'The password must contain at least one number',
  })
  .refine((password) => /[!@#$%^&*]/.test(password), {
    message: 'The password must contain at least one special character',
  });

// Mirrored server-side by `PasswordPolicyValidator` in
// services/api/native_auth/password_validation.py. Change both together.
export const NewPasswordForm = z
  .object({
    password: IdentityPassword,
    passwordConfirm: z.string({ required_error: 'The password confirmation is required' }),
  })
  .refine((data) => data.password === data.passwordConfirm, {
    message: 'The passwords do not match',
    path: ['passwordConfirm'],
  });
export type NewPasswordForm = z.infer<typeof NewPasswordForm>;

const TOO_MANY_ATTEMPTS = 'Too many attempts. Please wait a minute and try again.';

/**
 * The message the auth endpoints put in an error body: `detail` for 401/403
 * and some 400s, or the first message of the first field for a validation 400
 * (for example `{ "password": ["The password must contain at least one number."] }`).
 */
const messageFromBody = (data: unknown): string | undefined => {
  if (!data || typeof data !== 'object') {
    return undefined;
  }
  const { detail } = data as { detail?: unknown };
  if (typeof detail === 'string') {
    return detail;
  }
  for (const value of Object.values(data)) {
    if (Array.isArray(value) && typeof value[0] === 'string') {
      return value[0];
    }
  }
  return undefined;
};

export const getMessageFromError = (
  error: unknown,
  defaultMessage: string = 'An unknown error occurred',
) => {
  // An expected refusal (wrong password, expired link, throttled) is shown to
  // the user as the API worded it and is not reported to Sentry.
  if (error instanceof LiftAPIClientError && error.context.status < 500) {
    if (error.context.status === 429) {
      return TOO_MANY_ATTEMPTS;
    }
    return messageFromBody(error.context.data) ?? defaultMessage;
  }

  captureException(error);

  if (error instanceof Error) {
    return error.message;
  }

  if (typeof error === 'string') {
    return error;
  }

  return defaultMessage;
};
